/**
 * デモモード制限の案内表示コンポーネント
 * FL-154: デモ体験時の外部サービス無効化
 */

import React from 'react';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useDemoMode } from '../../hooks/useDemoMode';
import { DemoFeature } from '../../types/demoMode';

interface DemoModeNoticeProps {
  /** 表示タイプ */
  variant?: 'banner' | 'card' | 'inline';
  /** 表示する機能のリスト（指定しない場合は全制限機能を表示） */
  features?: DemoFeature[];
  /** 詳細表示するかどうか */
  showDetails?: boolean;
  /** カスタムクラス */
  className?: string;
}

/**
 * デモモード制限の案内表示コンポーネント
 */
export const DemoModeNotice: React.FC<DemoModeNoticeProps> = ({
  variant = 'banner',
  features,
  showDetails = false,
  className = '',
}) => {
  const { isEnabled, restrictedFeatures } = useDemoMode();

  // デモモードが無効の場合は何も表示しない
  if (!isEnabled) {
    return null;
  }

  // 表示対象の機能を決定
  const displayFeatures = features || restrictedFeatures;

  // 機能名の日本語マッピング
  const getFeatureName = (feature: DemoFeature): string => {
    switch (feature) {
      case DemoFeature.EMAIL_SEND:
        return 'メール送信';
      case DemoFeature.USER_REGISTRATION:
        return '新規ユーザー登録';
      case DemoFeature.PASSWORD_RESET:
        return 'パスワードリセット';
      case DemoFeature.AI_API:
        return 'AI機能';
      case DemoFeature.LINE_NOTIFICATION:
        return 'LINE通知';
      case DemoFeature.GOOGLE_MAPS:
        return 'Google Maps';
      case DemoFeature.SMS_SEND:
        return 'SMS送信';
      case DemoFeature.EXTERNAL_DATA_SYNC:
        return '外部データ同期';
      default:
        return String(feature).replace('_', ' ');
    }
  };

  // バナータイプのレンダリング
  if (variant === 'banner') {
    return (
      <div
        className={`bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-4 mb-4 ${className}`}
      >
        <div className='flex'>
          <div className='flex-shrink-0'>
            <ExclamationTriangleIcon className='h-5 w-5 text-amber-400' />
          </div>
          <div className='ml-3'>
            <p className='text-sm text-amber-700 dark:text-amber-200'>
              <span className='font-medium'>デモ環境</span> - 以下の機能は実際には実行されません
            </p>
            {showDetails && (
              <div className='mt-2 text-sm text-amber-600 dark:text-amber-300'>
                <ul className='list-disc list-inside space-y-1'>
                  {displayFeatures.map(feature => (
                    <li key={feature}>{getFeatureName(feature)}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // カードタイプのレンダリング
  if (variant === 'card') {
    return (
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-amber-200 dark:border-amber-800 p-6 ${className}`}
      >
        <div className='flex items-start'>
          <div className='flex-shrink-0'>
            <InformationCircleIcon className='h-6 w-6 text-amber-500' />
          </div>
          <div className='ml-4 flex-1'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
              デモ環境について
            </h3>
            <p className='text-sm text-gray-600 dark:text-gray-300 mb-4'>
              この環境では体験用のデータを使用しており、以下の機能は実際には実行されません。
            </p>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-500 dark:text-gray-400'>
              {displayFeatures.map(feature => (
                <div key={feature} className='flex items-center'>
                  <span className='w-2 h-2 bg-amber-400 rounded-full mr-2'></span>
                  {getFeatureName(feature)}
                </div>
              ))}
            </div>
            <div className='mt-4 text-xs text-gray-500 dark:text-gray-400'>
              実際の機能をお試しいただくには、本番環境をご利用ください。
            </div>
          </div>
        </div>
      </div>
    );
  }

  // インラインタイプのレンダリング
  return (
    <div
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 ${className}`}
    >
      <ExclamationTriangleIcon className='h-3 w-3 mr-1' />
      デモ環境
    </div>
  );
};

/**
 * 特定機能用のデモモード表示コンポーネント
 */
interface FeatureDemoNoticeProps {
  feature: DemoFeature;
  variant?: 'warning' | 'info';
  className?: string;
}

export const FeatureDemoNotice: React.FC<FeatureDemoNoticeProps> = ({
  feature,
  variant = 'warning',
  className = '',
}) => {
  const { isEnabled, getMessage } = useDemoMode();

  if (!isEnabled) {
    return null;
  }

  const message = getMessage(feature);

  const baseClasses =
    variant === 'warning'
      ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800'
      : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800';

  return (
    <div className={`rounded-md p-3 text-sm ${baseClasses} ${className}`}>
      <div className='flex items-start'>
        <div className='flex-shrink-0'>
          {variant === 'warning' ? (
            <ExclamationTriangleIcon className='h-4 w-4 mt-0.5' />
          ) : (
            <InformationCircleIcon className='h-4 w-4 mt-0.5' />
          )}
        </div>
        <div className='ml-2'>{message}</div>
      </div>
    </div>
  );
};

/**
 * ヘッダー用のシンプルなデモモード表示
 */
interface HeaderDemoNoticeProps {
  onResetDemo?: () => void;
}

export const HeaderDemoNotice: React.FC<HeaderDemoNoticeProps> = ({ onResetDemo }) => {
  const { isEnabled } = useDemoMode();

  if (!isEnabled) {
    return null;
  }

  return (
    <div className='bg-amber-500 px-4 py-2'>
      <div className='flex items-center justify-between max-w-7xl mx-auto'>
        <div className='flex-1 text-center'>
          <p className='text-sm text-white font-medium'>
            🧪 デモ環境 - 体験用データを使用しています
          </p>
        </div>
        {onResetDemo && (
          <button
            onClick={onResetDemo}
            className='ml-4 flex items-center gap-1 px-3 py-1 bg-amber-600 hover:bg-amber-700 rounded text-white text-xs font-medium transition-colors'
            title='デモデータを初期状態に戻します'
          >
            <svg
              className='h-3 w-3'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
              />
            </svg>
            データ初期化
          </button>
        )}
      </div>
    </div>
  );
};

export default DemoModeNotice;
